import { all, takeEvery, take, call, put, fork } from 'redux-saga/effects';
import { uploadFile, uploadFiles, uploadFailure, uploadSuccess, uploadProgress, uploadFileImmediately, cancelFileUpload } from '../actions';
import { createUploadChannel } from './base';
import { uploadQueueWatcher } from './queue';
import { fetchDeviation, putErrorNotification } from '../../../utils/saga';
import i18next from 'i18next';
import { getServerInstance } from '@wix/da-ddt';
const ddt = getServerInstance();
const DRAFTS_FOLDER_NAME = 'Saved Submissions';
const MEDIA_FOLDER_NAME = 'Media Uploads';
function* createDeviationUploadChannel({ file, deviationId, folderId, folderName, useDefaults, isPreviewUpload }) {
    const baseUrl = 'deviation/submit/upload';
    let url = deviationId ? `${baseUrl}/replace_original` : `${baseUrl}/deviation`;
    if (isPreviewUpload) {
        url = `${baseUrl}/replace_preview`;
    }
    const formData = new FormData();
    if (deviationId) {
        formData.append('deviationid', deviationId);
        if (isPreviewUpload) {
            formData.append('preview_file', file);
        } else {
            formData.append('original_file', file);
        }
    } else {
        formData.append('upload_file', file);
        formData.append('use_defaults', useDefaults);
        if (folderId) {
            formData.append('folderid', folderId);
        } else {
            formData.append('folder_name', folderName);
        }
    }
    return yield call(createUploadChannel, url, formData, 'deviation');
}
function isHEIC(file) {
    var _file_name_split_pop;
    const heicMimeType = 'image/heic';
    const heicExtensions = [
        'heic',
        'heif'
    ];
    const fileType = file.type.toLowerCase();
    const fileExtension = (_file_name_split_pop = file.name.split('.').pop()) === null || _file_name_split_pop === void 0 ? void 0 : _file_name_split_pop.toLowerCase();
    return fileType === heicMimeType || !!fileExtension && heicExtensions.includes(fileExtension);
}
async function convertHEICtoPNG(file) {
    if (typeof window === 'undefined') {
        return new Error('convertHEICtoPNG should only be called in the browser');
    }
    let heic2any;
    try {
        heic2any = (await import('heic2any')).default;
    } catch (e) {
        return new Error('Failed to import heic2any');
    }
    try {
        const conversionResult = await heic2any({
            blob: file,
            toType: 'image/png'
        });
        if (!(conversionResult instanceof Blob)) {
            return new Error('Failed to convert HEIC');
        }
        const pngFile = new File([
            conversionResult
        ], file.name.replace(/\.[^/.]+$/, '.png'), {
            type: 'image/png'
        });
        return pngFile;
    } catch (e) {
        return new Error('Failed to convert HEIC');
    }
}
function* uploadFileHandler(action) {
    const { file, uploadId, deviationId, folderId, autoFetchDeviationOnSuccess, isDraft, useDefaults, onSuccess, onFailure, isPreviewUpload } = action.payload;
    const fileToUpload = isHEIC(file) ? yield call(convertHEICtoPNG, file) : file;
    if (fileToUpload instanceof Error) {
        ddt.alert('submit', 'Failed to convert HEIC to PNG', [
            'submit'
        ], {});
        yield putErrorNotification(i18next.t('upload.error.heic'));
        yield put(uploadFailure(uploadId, {
            isServerError: true,
            name: 'HEIC',
            message: i18next.t('upload.error.heic')
        }));
        return;
    }
    // Unless folderid is specified, the folder will be set by name, which will be one of these 3:
    // - "PCP attachments" if it's a pcp asset
    // - "Saved Submissions" if it's a draft upload on studio
    // - "Media Uploads" if it's anything else (e.g. editor uploads)
    // - Stash protect folder name, as defined in `useFileUpload` hook
    const folderName = action.payload.folderName || (isDraft ? DRAFTS_FOLDER_NAME : MEDIA_FOLDER_NAME);
    const channel = yield call(createDeviationUploadChannel, {
        file: fileToUpload,
        deviationId,
        folderId,
        folderName,
        useDefaults,
        isPreviewUpload
    });
    // If a cancel action for this file id is fired we close the channel
    yield fork(function*() {
        yield take(({ type, payload })=>type === cancelFileUpload.toString() && payload.uploadId === uploadId);
        channel.close();
    });
    while(true){
        const { progress = 0, error, success, response } = yield take(channel);
        if (error) {
            yield put(uploadFailure(uploadId, error));
            if (onFailure) {
                yield call(onFailure, {
                    uploadId
                }, error);
            }
            return;
        }
        if (success) {
            const uploadedDeviationId = deviationId ? response === null || response === void 0 ? void 0 : response.deviationId : response === null || response === void 0 ? void 0 : response.privateId;
            yield put(uploadSuccess(uploadId, uploadedDeviationId));
            if (onSuccess) {
                let deviation;
                if (autoFetchDeviationOnSuccess && uploadedDeviationId) {
                    const fetchedDeviation = yield call(fetchDeviation, uploadedDeviationId);
                    if (fetchedDeviation) {
                        deviation = fetchedDeviation;
                    }
                }
                yield call(onSuccess, {
                    uploadId,
                    file: fileToUpload,
                    deviationId: uploadedDeviationId,
                    deviation
                });
            }
            return;
        }
        yield put(uploadProgress(uploadId, progress));
    }
}
export default function* saga() {
    yield all([
        takeEvery(uploadFileImmediately, uploadFileHandler),
        uploadQueueWatcher('deviation', [
            uploadFile,
            uploadFiles,
            uploadSuccess,
            uploadFailure
        ], uploadFileImmediately, true // turn on sequenced uploads
        )
    ]);
}

//# sourceMappingURL=deviations.js.map